/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useEffect, useRef, useState } from 'react';

// Mantine
import {
	Box,
	rem,
	Image,
	Group,
	Checkbox,
	Text,
	Flex,
} from '@mantine/core';

// Styles
import classes from './CellGlobalActions.module.css';

import {
	randomId,
	useClickOutside,
	useDisclosure,
	useListState,
} from '@mantine/hooks';

import {
	IconGripVertical,
	IconMenu2,
} from '@tabler/icons-react';
import {
	DragDropContext,
	Draggable,
	Droppable,
} from '@hello-pangea/dnd';

import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

export const CellGlobalActions = (props: {
	allColumns?: any;
	columns?: any;
	disabled?: any;
	handlechangecolumns?: (col: any) => void;
}) => {
	const { t } = useTranslation();
	const { columns, allColumns } = props;
	const isMounted = useRef(false);
	const [opened, setOpened] = useState(false);
	const ref = useClickOutside(() => setOpened(false));

	const sortedColumns =
		allColumns && Array.isArray(allColumns)
			? allColumns.sort((a, b) => {
					const indexA = columns.findIndex(
						(col: any) => col.accessor === a.accessor
					);
					const indexB = columns.findIndex(
						(col: any) => col.accessor === b.accessor
					);
					if (
						a.accessor === 'actions' ||
						a.accessor === 'expand' ||
						a.accessor === 'quantityCheck'
					)
						return 1;
					if (
						b.accessor === 'actions' ||
						a.accessor === 'expand' ||
						a.accessor === 'quantityCheck'
					)
						return -1;

					if (indexA === -1) return 1;
					if (indexB === -1) return -1;

					return indexA - indexB;
				})
			: [];

	const outputData =
		allColumns &&
		columns &&
		Array.isArray(allColumns) &&
		Array.isArray(columns)
			? sortedColumns.map((item, index) => ({
					position: index + 1,
					checked: columns?.some(
						(col) => col.accessor === item.accessor
					),
					key: randomId(),
					name: item.accessor,
					title: item.title,
					defaultVisible: item.defaultVisible,
					fixed: item.fixed,
				}))
			: [];
	const [state, handlers] = useListState(outputData);

	useEffect(() => {
		if (isMounted.current) {
			if (
				state &&
				Array.isArray(state) &&
				props.handlechangecolumns
			) {
				props.handlechangecolumns(state);
			}
		} else {
			isMounted.current = true;
		}
	}, [state]);

	const [openedColumnsActions, handlersColumnsActions] =
		useDisclosure(false);

	const refContainerItems = useClickOutside(() =>
		handlersColumnsActions.close()
	);

	return (
		<Box ref={refContainerItems}>
			<DragDropContext
				onDragEnd={({ destination, source }) => {
					return handlers.reorder({
						from: source.index,
						to: destination?.index || 0,
					});
				}}
			>
				<Box>
					{!props.disabled && (
						<Flex justify={'flex-end'}>
							<IconMenu2
								onClick={() =>
									handlersColumnsActions.toggle()
								}
								style={{
									objectFit: 'contain',
									width: rem(20),
									height: rem(20),
									cursor: 'pointer',
									opacity: 1,
								}}
							/>
						</Flex>
					)}
					{props.disabled && (
						<Image
							style={{
								objectFit: 'contain',
								width: rem(20),
								height: rem(20),
								opacity: 0.5,
							}}
							src={IconMenu2}
						/>
					)}

					<Box
						ref={ref}
						className={clsx(classes.containerMenu)}
						style={{
							display: openedColumnsActions
								? 'flex'
								: 'none',
						}}
					>
						<Droppable
							droppableId="columns-list"
							direction="vertical"
						>
							{(droppableProvided) => (
								<Flex
									{...droppableProvided.droppableProps}
									ref={droppableProvided.innerRef}
									direction={'column'}
									gap={'var(--mantine-spacing-xs)'}
								>
									<Group
										p={'5px'}
										justify="space-between"
									>
										<Group gap={'5px'}>
											<Text
												className={classes.title}
												onClick={(e) => {
													e.stopPropagation();
												}}
											>
												{t('global.displayedInTheTable')}
											</Text>
										</Group>
										<Text
											className={classes.textAction}
											onClick={(e) => {
												e.stopPropagation();
												handlers.setState(
													state.map((item) => ({
														...item,
														checked:
															item.name === 'actions' ||
															item.name === 'expand' ||
															item.name ===
																'quantityCheck' ||
															item.fixed
																? true
																: item.defaultVisible,
													}))
												);
											}}
										>
											{t('global.reset')}
										</Text>
									</Group>

									{state
										.filter(
											(item: any) =>
												item.name !== 'actions' &&
												item.name !== 'expand' &&
												item.name !== 'quantityCheck'
										)
										.map((item, index) => (
											<Draggable
												key={item.key}
												index={
													state?.some(
														(item) =>
															item.name === 'expand'
													)
														? index + 1
														: index
												}
												draggableId={item.key}
											>
												{(draggableProvided) => (
													<Box
														onClick={(e) => {
															e.stopPropagation();

															handlers.setItemProp(
																state?.some(
																	(item) =>
																		item.name === 'expand'
																)
																	? index + 1
																	: index,
																'checked',
																item.name === 'actions' ||
																	item.name ===
																		'expand' ||
																	item.fixed
																	? true
																	: !item.checked
															);
														}}
														ref={
															draggableProvided.innerRef
														}
														{...draggableProvided.draggableProps}
													>
														<Group
															justify="space-between"
															className={
																classes.dragHandle
															}
															{...draggableProvided.dragHandleProps}
														>
															<Group
																style={{
																	display: 'flex',
																	flex: '1',
																	height: '100%',
																}}
																gap={'5px'}
															>
																<IconGripVertical
																	size={16}
																	stroke={1.5}
																/>
																<Text
																	style={{
																		flex: '1',
																	}}
																	onClick={(e) => {
																		e.stopPropagation();

																		handlers.setItemProp(
																			state?.some(
																				(item) =>
																					item.name ===
																					'expand'
																			)
																				? index + 1
																				: index,
																			'checked',
																			item.name ===
																				'actions' ||
																				item.name ===
																					'expand' ||
																				item.fixed
																				? true
																				: !item.checked
																		);
																	}}
																	className={
																		classes.textItem
																	}
																>
																	{t(item.title)}
																</Text>

																{!item.fixed && (
																	<Checkbox
																		variant="outline"
																		radius={'5px'}
																		color="var(--mantine-color-igluPrimary-9)"
																		style={{
																			pointerEvents:
																				'none',
																		}}
																		checked={item.checked}
																		onChange={() => {}}
																		size="17px"
																		onClick={(e) => {
																			e.stopPropagation();
																			handlers.setItemProp(
																				state?.some(
																					(item) =>
																						item.name ===
																						'expand'
																				)
																					? index + 1
																					: index,
																				'checked',
																				e.currentTarget
																					.checked
																			);
																		}}
																	/>
																)}
															</Group>
														</Group>
													</Box>
												)}
											</Draggable>
										))}
									{droppableProvided.placeholder}
								</Flex>
							)}
						</Droppable>
					</Box>
				</Box>
			</DragDropContext>
		</Box>
	);
};
